/**
 * Camera controller — third-person orbit follow, plus the free/killer camera the
 * death cam drives.
 *
 * Pre-allocates its Vector3s: this runs every frame and per-frame allocation in
 * the camera path is the cheapest GC pause to avoid.
 *
 * The camera's yaw is also the player's AIM. `getYaw()` is what inputSender puts
 * in the InputFrame, which is why nothing else may write `cameraState.yaw`.
 */

import * as THREE from 'three/webgpu';
import { PHYSICS } from '@chaos/sim';
import { CHARACTERS } from '@chaos/protocol';
import {
  CAMERA_LOOK_HEIGHT,
  SPRINT_FOV_BOOST,
  FREE_FLY_SPEED,
  FREE_FAST_SPEED,
  isTouch,
} from './config.js';
import { cameraState, freeCamPos, cameraShake, localPlayer, remotePlayers, session } from './state.js';
import { updateCameraShake } from './vfx/ScreenEffects.js';
import { clamp } from './math.js';

/**
 * Where the ground is, for the camera's floor clamp. Set from `world_init`;
 * 0 is the flat bowl's deck and a safe default for a map that never reports.
 */
let _groundY = 0;

/** Told by the client once the map is known. */
export function setCameraGroundLevel(y) {
  _groundY = Number.isFinite(y) ? y : 0;
}

/**
 * How far above the ground the camera is allowed to get.
 *
 * The orbit maths put it underground at any low pitch: at MIN_PITCH (-30°) the
 * vertical offset is sin(-30°) * distance, so at distance 15 the camera sits
 * 7.5 below the player and ends up 4.5 units UNDER the deck — you see the
 * underside of the platforms and straight through the floor. That was true at
 * every zoom level (even the closest put it at -1) and at every distance this
 * game has shipped, so it is the clamp that was missing rather than the pitch
 * limit that was wrong.
 */
const CAMERA_FLOOR_CLEARANCE = 0.9;

/**
 * The fixed frame. There is no camera input on this branch at all.
 *
 * Brawl Stars' readability comes from removing the camera as a thing you
 * manage: one fixed angle, one fixed zoom, always centred on you. Every
 * "orientation" and "depth" complaint the orbiting build produced was a
 * camera-management problem, and on a phone a free orbit needs a third thumb
 * that does not exist.
 *
 * 55° rather than straight down: enough tilt to keep character silhouettes and
 * their facing readable, enough height that the ground plane reads as a plane
 * and distances on it are honest.
 *
 * FRAMING. A 2-unit body covers 2 / (2*d*tan(fov/2)) of the screen, and the
 * ground depth in view is that height divided by sin(pitch). Brawl Stars sits
 * near 12% of screen height and about 20 body-widths of ground — close enough
 * to read a face, wide enough to see someone coming.
 *
 * LENS. Matching those two numbers is not the same as matching the SHOT, which
 * is what was still off after the first pass. Framing was right at 75 degrees
 * and d=11 and it still did not look like Brawl Stars, because 75 is a wide
 * lens: verticals splay toward the corners, a wall on the left leans left, and
 * the deck reads as a bowl. Brawl Stars is close to orthographic — a long lens
 * pulled back, so the arena reads flat and a platform on the rim is the same
 * shape as one at your feet.
 *
 * So the lens gets longer and the camera steps back to hold the framing exactly:
 *
 *     2*d*tan(fov/2) is the visible height; keep it constant.
 *     fov 75, d=11.0 -> 16.88 units visible, 11.8% body, ~20 body-widths
 *     fov 50, d=18.1 -> 16.88 units visible, 11.8% body, ~20 body-widths
 *
 * Identical subject size and identical ground extent. The only thing that
 * changed is perspective divergence, which is the thing that was wrong.
 *
 * 55° pitch rather than straight down: enough tilt to keep silhouettes and
 * facing readable, enough height that the ground plane reads as a plane.
 */
const TOPDOWN_PITCH = (55 * Math.PI) / 180;
const TOPDOWN_YAW = 0;
/** These two are one setting. Changing either alone changes the framing. */
/**
 * Below this speed the body is not really going anywhere, so it keeps the
 * facing it had. Well under WALK_SPEED, and above the drift a reconciliation
 * nudge produces while standing still.
 */
const HEADING_FACING_MIN_SPEED = 1.5;

const TOPDOWN_FOV = 50;
const TOPDOWN_DISTANCE = 18.1;

/*
 * The camera offset, resolved once.
 *
 * This was four trig calls per frame. That was necessary when pitch and yaw
 * came from mutable `cameraState`; they are compile-time constants now, so it
 * was computing the same three numbers sixty times a second forever.
 */
const OFFSET_X = Math.sin(TOPDOWN_YAW) * Math.cos(TOPDOWN_PITCH) * TOPDOWN_DISTANCE;
const OFFSET_Y = Math.sin(TOPDOWN_PITCH) * TOPDOWN_DISTANCE;
const OFFSET_Z = Math.cos(TOPDOWN_YAW) * Math.cos(TOPDOWN_PITCH) * TOPDOWN_DISTANCE;

export class CameraController {
  constructor(threeCamera, renderer) {
    this.camera = threeCamera;
    this.renderer = renderer;
    // Owned here, not in SceneSetup: the FOV and the distance are a single
    // setting (see above) and splitting them across two files is how one of
    // them gets changed on its own.
    this.camera.fov = TOPDOWN_FOV;
    this.camera.updateProjectionMatrix();

    this._look = new THREE.Vector3();
    this._forward = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._move = new THREE.Vector3();
    this._target = new THREE.Vector3();
    /** Cursor in normalized device coords, and whether we have ever seen it. */
    this._ndc = new THREE.Vector2(0, 0);
    this._haveMouse = false;
    /** Aim direction as a yaw. Held so a frame with no mouse motion keeps it. */
    this._aimYaw = 0;
    /** Where the aim ray meets the ground — the client draws its reticle here. */
    this.aimPoint = new THREE.Vector3();
    this._ray = new THREE.Raycaster();
    /** Right-stick aim on touch, or null when the thumb is off it. */
    this._aimStick = null;

    /** FOV punch, driven by dashes and hits. Decays back to base every frame. */
    this._baseFov = threeCamera.fov;
    this._fovPunch = 0;
    /** Speed-based FOV widening: walking is the base lens, sprinting is +SPRINT_FOV_BOOST. */
    this._speedFov = 0;
    this._prevRenderPos = new THREE.Vector3();
    this._haveRenderPos = false;

    /** Smoothed heading from actual travel, for facing when nothing is aiming. */
    this._headingX = 0;
    this._headingZ = -1;
    this._headingSpeed = 0;
    this._prevHeadingPos = new THREE.Vector3();
    this._haveHeadingPos = false;
  }

  /**
   * Which way the body is actually travelling, smoothed.
   *
   * Measured from the rendered position rather than read off an input vector,
   * so it already accounts for the sim: a body sliding on ice, shoved by a
   * knockback, or pressed along a wall faces where it is GOING, not where the
   * stick is pushing.
   */
  _updateHeading(target, delta) {
    if (delta <= 0) return;
    if (!this._haveHeadingPos) {
      this._prevHeadingPos.copy(target);
      this._haveHeadingPos = true;
      return;
    }
    const dx = target.x - this._prevHeadingPos.x;
    const dz = target.z - this._prevHeadingPos.z;
    this._prevHeadingPos.copy(target);

    const dist = Math.hypot(dx, dz);
    const speed = dist / delta;
    // A spawn teleport or a reconciliation hard-snap is not travel, and turning
    // to face one would spin the body for a frame.
    if (speed > 80 || dist < 1e-6) {
      this._headingSpeed = 0;
      return;
    }
    this._headingSpeed = speed;
    // Exponential approach, ~80ms: fast enough that a direction change reads as
    // immediate, slow enough that per-frame jitter does not shiver the model.
    const k = 1 - Math.exp(-delta / 0.08);
    this._headingX += (dx / dist - this._headingX) * k;
    this._headingZ += (dz / dist - this._headingZ) * k;
  }

  initDesktopEvents() {
    if (isTouch) return;
    const dom = this.renderer.domElement;

    // No pointer lock, so no lock hint and no centre crosshair: capturing the
    // cursor would take away the one thing the player aims with, and a fixed
    // crosshair in the middle of the screen would point at the camera's centre
    // rather than at the character's aim. The reticle is drawn on the GROUND
    // where the aim actually lands — see `aimPoint`.

    // The mouse AIMS; it does not look. Position, not movementX/Y — there is no
    // pointer lock here, so the cursor stays visible and its place on the
    // ground plane is the thing the character points at.
    document.addEventListener('mousemove', (e) => {
      this._ndc.set(
        (e.clientX / window.innerWidth) * 2 - 1,
        -(e.clientY / window.innerHeight) * 2 + 1
      );
      this._haveMouse = true;
      // The free (death) camera still drags to look — it is a spectator tool
      // and has no aim to express.
      if (cameraState.free && this._dragging) {
        cameraState.yaw -= e.movementX * 0.003;
        cameraState.pitch -= e.movementY * 0.003;
        cameraState.pitch = clamp(cameraState.pitch, -Math.PI / 2 + 0.01, Math.PI / 2 - 0.01);
      }
    });

    dom.addEventListener('mousedown', (e) => {
      if (e.button === 0) {
        this._dragging = true;
        // Flag the drag on the SAME mousedown that starts it: this canvas
        // listener fires at the target before the event bubbles to
        // InputManager's document-level one, which suppresses the LMB attack
        // while the flag is up.
        // Only the spectator camera drags to look; in play LMB is the attack.
      }
    });
    document.addEventListener('mouseup', () => {
      this._dragging = false;
    });

    // No zoom either: a fixed frame is only fixed if everyone has the same one,
    // and a player who zooms out for map awareness would simply be right.
  }

  /**
   * The browser refused pointer lock.
   *
   * A sandboxed iframe without `allow-pointer-lock` is the case that matters: the
   * request throws and mouse look would simply never work, with nothing on screen
   * to say why. Dragging with the left button held takes over instead — the path
   * the free camera already uses. It overlaps with the basic attack, which is the
   * price of a camera that turns at all.
   */
  /** A short FOV widening. Dash uses ~6 degrees; a heavy hit uses ~3. */
  punchFov(degrees) {
    this._fovPunch = Math.max(this._fovPunch, degrees);
  }

  update(delta) {
    this._updateSpeedFov(delta);
    if (this._fovPunch > 0.01) this._fovPunch *= Math.pow(0.001, delta);
    else this._fovPunch = 0;

    // Speed widening and the punch compose additively on the base lens.
    const fov = this._baseFov + this._speedFov + this._fovPunch;
    if (Math.abs(this.camera.fov - fov) > 0.001) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }

    if (cameraState.free) {
      this._updateFreeCamera();
      return;
    }
    this._updateFollowCamera(delta);
  }

  /**
   * Widens the lens up to +SPRINT_FOV_BOOST degrees as the body's horizontal
   * speed goes walk → sprint — the felt contrast between the two gaits, read
   * from the rendered position so it needs nothing from the netcode. Free cam,
   * death cam and death all leave `renderPos` still, which zeroes it for free.
   */
  _updateSpeedFov(delta) {
    if (delta <= 0) return;
    const p = localPlayer.renderPos;
    if (!this._haveRenderPos) {
      this._prevRenderPos.copy(p);
      this._haveRenderPos = true;
      return;
    }
    const dx = p.x - this._prevRenderPos.x;
    const dz = p.z - this._prevRenderPos.z;
    this._prevRenderPos.copy(p);
    let speed = Math.sqrt(dx * dx + dz * dz) / delta;
    // A spawn teleport or a reconciliation hard-snap is not motion.
    if (speed > 80) speed = 0;

    const mult = CHARACTERS[session.characterId]?.speedMult ?? 1;
    const walk = PHYSICS.WALK_SPEED * mult;
    const sprint = PHYSICS.SPRINT_SPEED * mult;
    const frac = clamp((speed - walk) / (sprint - walk), 0, 1);
    // Exponential approach, ~0.1s time constant: with the sim's decel from 26
    // to 16 in front of it, a Shift release settles back in ~0.3s.
    const k = 1 - Math.exp(-delta / 0.1);
    this._speedFov += (SPRINT_FOV_BOOST * frac - this._speedFov) * k;
    if (this._speedFov < 0.001) this._speedFov = 0;
  }

  _followTarget() {
    // The death cam parks `followId` on the killer; otherwise we follow the body.
    if (cameraState.followId) {
      const mesh = remotePlayers.get(cameraState.followId);
      if (mesh) return this._target.copy(mesh.position);
    }
    return this._target.copy(localPlayer.renderPos);
  }

  _updateFollowCamera(delta) {
    const target = this._followTarget();

    // Fixed. Not read from `cameraState` — nothing writes it any more, and
    // reading it would leave a door open for something to start.

    updateCameraShake();
    this.camera.position.set(
      target.x + OFFSET_X + cameraShake.offset.x,
      Math.max(target.y + OFFSET_Y + cameraShake.offset.y, _groundY + CAMERA_FLOOR_CLEARANCE),
      target.z + OFFSET_Z + cameraShake.offset.z
    );
    this.camera.lookAt(target.x, target.y + CAMERA_LOOK_HEIGHT, target.z);

    this._updateHeading(target, delta);
    this._updateAim(target);
  }

  /**
   * Turns the cursor (or the right stick) into a facing.
   *
   * The ground plane at the player's feet is the aim surface, so pointing at a
   * spot on the floor means exactly that — no depth guess, no crosshair
   * floating in space. `forwardFromYaw` is (-sin, -cos), so the yaw that faces
   * a delta is atan2(-dx, -dz); getting that sign wrong is the bug that once
   * had bots walking away from their targets.
   */
  _updateAim(target) {
    if (this._aimStick) {
      const { x, z } = this._aimStick;
      if (Math.hypot(x, z) > 0.01) {
        this._aimYaw = Math.atan2(-x, -z);
        this.aimPoint.set(target.x + x * 6, target.y, target.z + z * 6);
      }
      return;
    }
    // NOTHING IS AIMING — so face where you are walking.
    //
    // On a phone there is never a mouse, and the aim stick only reports while a
    // thumb is actually dragging. So `_aimYaw` sat at 0 and the character never
    // turned at all: you could walk a full circle facing due north the whole
    // way. Reported straight from a playtest as "characters are not rotating".
    //
    // Brawl Stars behaves this way too, and it is the right default rather than
    // a patch: your body follows your feet until you say otherwise, and the
    // moment you aim, aim wins. On desktop `_haveMouse` latches true at the
    // first mouse move and never unlatches, so this changes nothing there.
    if (!this._haveMouse) {
      if (this._headingSpeed > HEADING_FACING_MIN_SPEED) {
        this._aimYaw = Math.atan2(-this._headingX, -this._headingZ);
        this.aimPoint.set(
          target.x + this._headingX * 6,
          target.y,
          target.z + this._headingZ * 6
        );
      }
      return;
    }

    this._ray.setFromCamera(this._ndc, this.camera);
    const dir = this._ray.ray.direction;
    // Where the ray crosses the player's own ground height.
    if (Math.abs(dir.y) < 1e-6) return;
    const t = (target.y - this._ray.ray.origin.y) / dir.y;
    if (t <= 0) return;
    this.aimPoint.copy(this._ray.ray.origin).addScaledVector(dir, t);

    const dx = this.aimPoint.x - target.x;
    const dz = this.aimPoint.z - target.z;
    if (dx * dx + dz * dz < 1e-4) return;
    this._aimYaw = Math.atan2(-dx, -dz);
  }

  /** Called by MobileControls with the right stick, or null on release. */
  setAimStick(vector) {
    this._aimStick = vector;
  }

  _updateFreeCamera() {
    updateCameraShake();
    // Same floor for the death cam: a spectator flying under the arena sees the
    // match through the deck, which is both ugly and a small wallhack.
    freeCamPos.y = Math.max(freeCamPos.y, _groundY + CAMERA_FLOOR_CLEARANCE);
    this.camera.position.copy(freeCamPos).add(cameraShake.offset);
    this._look.set(
      freeCamPos.x - Math.sin(cameraState.yaw) * Math.cos(cameraState.pitch),
      freeCamPos.y - Math.sin(cameraState.pitch),
      freeCamPos.z - Math.cos(cameraState.yaw) * Math.cos(cameraState.pitch)
    );
    this.camera.lookAt(this._look);
  }

  /**
   * Camera-space basis for movement intent.
   *
   * The sim rotates the intent by the player's own yaw (movement.ts), and the
   * player's yaw is this camera's yaw, so the client sends the intent in the
   * SAME frame the sim expects: +Z forward, +X right. No world-space conversion
   * happens on the client — that is the server's job with its own yaw.
   */
  /**
   * What `inputSender` puts in the InputFrame — and therefore the player's
   * facing in the simulation.
   *
   * On the 3rd-person build this was the camera's yaw, because the camera WAS
   * the aim. Here the camera has no yaw to speak of and this is where the
   * mouse points instead.
   */
  getYaw() {
    return cameraState.free ? cameraState.yaw : this._aimYaw;
  }

  updateFreeMovement(delta, keys) {
    if (!cameraState.free) return;
    const speed = keys.shift ? FREE_FAST_SPEED : FREE_FLY_SPEED;
    this._forward
      .set(
        -Math.sin(cameraState.yaw) * Math.cos(cameraState.pitch),
        -Math.sin(cameraState.pitch),
        -Math.cos(cameraState.yaw) * Math.cos(cameraState.pitch)
      )
      .normalize();
    this._right.set(-this._forward.z, 0, this._forward.x).normalize();
    this._move.set(0, 0, 0);
    if (keys.w) this._move.add(this._forward);
    if (keys.s) this._move.sub(this._forward);
    if (keys.a) this._move.sub(this._right);
    if (keys.d) this._move.add(this._right);
    if (keys.space) this._move.y += 1;
    if (keys.ctrl) this._move.y -= 1;
    if (this._move.lengthSq() > 0) {
      this._move.normalize().multiplyScalar(speed * delta);
      freeCamPos.add(this._move);
    }
  }

  /** Hand the free camera the position it should start from. */
  detachToFreeCamera() {
    freeCamPos.copy(this.camera.position);
    cameraState.free = true;
    cameraState.followId = null;
  }

  attachToPlayer() {
    cameraState.free = false;
    cameraState.followId = null;
  }
}

/**
 * The aim dot. index.html ships it with CSS; gym.html does not, so the same
 * 6px dot (v1 game.css:1109-1122) is built inline when the markup is absent.
 */

